import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';

type ChangeFilter = {
  event?: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
  schema?: string;
  table: string;
  filter?: string;
};

/**
 * Safe postgres_changes subscription.
 *
 * Supabase reuses channels by name. React StrictMode (and fast remounts)
 * can call `.on()` on an already-subscribed channel, which throws:
 *   "cannot add postgres_changes callbacks … after subscribe()"
 * and takes down the ErrorBoundary.
 *
 * Always mint a unique channel name, attach handlers before subscribe,
 * and never let realtime failures crash the UI.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RealtimePayload = any;

export function subscribeTable(
  supabase: SupabaseClient,
  baseName: string,
  filters: ChangeFilter | ChangeFilter[],
  onChange: (payload?: RealtimePayload) => void,
): () => void {
  const list = Array.isArray(filters) ? filters : [filters];
  const name = `${baseName}:${Math.random().toString(36).slice(2, 10)}`;

  let channel: RealtimeChannel | null = null;
  try {
    channel = supabase.channel(name);
    for (const f of list) {
      channel = channel.on(
        'postgres_changes',
        {
          event: f.event ?? '*',
          schema: f.schema ?? 'public',
          table: f.table,
          ...(f.filter ? { filter: f.filter } : {}),
        },
        (payload) => {
          try {
            onChange(payload);
          } catch {
            /* ignore handler errors */
          }
        },
      );
    }
    channel.subscribe();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[realtime] subscribe failed', baseName, err);
    channel = null;
  }

  return () => {
    if (!channel) return;
    try {
      void supabase.removeChannel(channel);
    } catch {
      /* ignore */
    }
  };
}
